import ZenotiConnection from "./ZenotiConnection";
import WorkflowPacket from "@/components/documents/WorkflowPacket";
import ResponsivePageNavigation from "@/components/toto/ResponsivePageNavigation";
import PersonalProfileSections from "@/components/toto/PersonalProfileSections";
import EmployeeRecordFile from "@/components/EmployeeRecordFile";
import EmployeeFileDrawer from "@/components/EmployeeFileDrawer";
import OpenButton from "@/components/toto/OpenButton";
import React, { useEffect, useRef, useState } from "react";
import { Check, FileText, ShieldCheck, Upload, UserRound, Settings, } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProfilePreferences } from "./BackgroundSettings";
import { toast } from "sonner";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import TotoIdentityCard from "@/components/toto/TotoIdentityCard";
import "./toto-profile.css";
import "./quiet-sections.css";
import "./profile-bento.css";
import "./profile-approved-refinement.css";
import "./profile-balanced-overview.css";
import "./profile-material.css";

export default function TotoProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [actionsHost,setActionsHost]=useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = ["account", "file", "documents", "preferences", "connections"].includes(searchParams.get("view")) ? searchParams.get("view") : "account";
  const setView = (next) => setSearchParams({ view: next });
  const [recordEnabled,setRecordEnabled]=useState(false);
  useEffect(()=>{api.get("/employee-records/capabilities").then(r=>setRecordEnabled(r.data.enabled===true&&r.data.can_self===true)).catch(()=>setRecordEnabled(false));},[]);
  const [packet, setPacket] = useState({ documents: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const profile = {...user, ...(user?.employee_profile || {}), user_id:user?.user_id};
  const boutique = profile.boutique_name || (profile.boutique_code ? boutiqueOf(profile.boutique_code).name : "Not assigned");
  const [uploading, setUploading] = useState("");
  const [documentIndex, setDocumentIndex] = useState(0);
  const title = profile.directory_role || profile.title || user?.role?.replaceAll("_", " ") || "Team member";

  const loadPacket = async () => {
    try { setPacket((await api.get("/onboarding-documents/me")).data); }
    catch { setPacket({ documents: [], pending_count: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPacket(); }, []);

  const submit = async (form, file) => {
    if (!file) return;
    setUploading(form.id);
    const body = new FormData();
    body.append("file", file);
    body.append("category", form.category);
    try {
      await api.post(`/employee-documents/${user.user_id}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted to your confidential employee file");
      await loadPacket();
    } catch (e) { toast.error(e.response?.data?.detail || "The form could not be submitted"); }
    finally { setUploading(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const pending = packet.pending_count || 0;
  const documentCount = packet.documents.length;
  const focusedDocument = packet.documents[Math.min(documentIndex, Math.max(documentCount - 1, 0))];

  return <main className={`toto-profile${view==='file'&&recordEnabled?' toto-profile--compact-file':''}`} data-testid="toto-profile">
    <section className="toto-profile__vessel toto-spatial-plane" aria-labelledby="profile-title">
      <div className="toto-vessel-content-slot" data-vessel-content-slot>
      <header className="toto-profile__header">
        <h1 id="profile-title">My Profile</h1>
        {["preferences","file"].includes(view) && <time className="preferences-header-date" dateTime={new Date().toLocaleDateString("en-CA")}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</time>}
        <div className="toto-profile__edit-toolbar" ref={setActionsHost} />
      </header>
      <ResponsivePageNavigation label="Profile pages" current={view} items={[
        {id:'account',label:'Overview',onSelect:()=>setView('account')},
        {id:'file',label:'Employee file',onSelect:()=>setView('file')},
        {id:'documents',label:'Onboarding',detail:pending>0?`${pending} ready to complete`:undefined,onSelect:()=>setView('documents')},
        {id:'preferences',label:'Preferences',onSelect:()=>setView('preferences')},
        {id:'connections',label:'Connections',onSelect:()=>setView('connections')},
      ]} footer={<button type="button" aria-label="Sign out of TOTO" onClick={logout}>Sign out</button>}>
      <nav className="profile-vessel-navigation" aria-label="Your account and personal tools">
        {[['account','Overview',UserRound],['file','Employee file',FileText],['documents','Onboarding',ShieldCheck],['preferences','Preferences',Settings],['connections','Connections',ShieldCheck]].map(([key,label,Icon])=><button key={key} type="button" aria-current={view === key ? 'page' : undefined} className="toto-profile__account-link" onClick={()=>setView(key)}><Icon size={20} aria-hidden="true"/><span>{label}{key === 'documents' && pending > 0 && <small>{pending} ready to complete</small>}</span></button>)}
        <button type="button" className="profile-navigation-signout" aria-label="Sign out of TOTO" onClick={logout}>Sign out</button></nav></ResponsivePageNavigation>
      <div className="toto-profile__content" key={view}>
      <div className={`toto-profile__identity-layout ${view === "account" ? "is-quiet-profile is-bento-profile" : ["preferences", "connections"].includes(view) ? "is-preferences-layout" : ""}`}>
        <div className={`toto-profile__account-pane ${view === "documents" ? "profile-onboarding-pane" : ""}`}>
      {view === "account" ? <>
        <PersonalProfileSections actionsHost={actionsHost} identityArea={<TotoIdentityCard person={profile} name={profile.name || "Your profile"} title={title}><dl className="profile-overview-account"><div><dt>Boutique</dt><dd>{boutique}</dd></div><div><dt>Email</dt><dd>{user?.email || "Not available"}</dd></div></dl></TotoIdentityCard>} />
      </> : view === "file" ? recordEnabled ? <EmployeeRecordFile self employeeId="me"/> : <EmployeeFileDrawer employeeId="me" readOnly embedded /> : view === "preferences" ? <ProfilePreferences /> : view === "connections" ? <ZenotiConnection embedded/> : <>
        <h2 className="profile-section-title">Onboarding</h2>
        <WorkflowPacket/>
        {documentCount > 0 && <h3>Other forms</h3>}
        {loading && <p className="toto-profile__quiet">Checking your assigned documents…</p>}

        <div className="toto-profile__document-list">
          {focusedDocument && <article key={focusedDocument.id} className={`toto-profile__document ${focusedDocument.status === "submitted" ? "is-submitted" : ""}`}>
            <div><p>{focusedDocument.status === "submitted" ? <Check size={15} aria-hidden="true" /> : <FileText size={15} aria-hidden="true" />}{focusedDocument.label}</p><small>{focusedDocument.status === "submitted" ? "Submitted to your file" : focusedDocument.detail}</small></div>
            {focusedDocument.status === "submitted" ? <span className="toto-profile__submitted">On file</span> : <div className="toto-profile__document-actions"><OpenButton href={focusedDocument.template_url} target="_blank" rel="noreferrer" aria-label="Open form"/><button type="button" onClick={() => { fileRef.current.dataset.formId = focusedDocument.id; fileRef.current.click(); }} disabled={uploading === focusedDocument.id}><Upload size={13} aria-hidden="true" />{uploading === focusedDocument.id ? "Submitting…" : "Submit PDF"}</button></div>}
          </article>}
          {documentCount > 1 && <div className="toto-profile__document-pagination"><span>{documentIndex + 1} of {documentCount}</span><button type="button" onClick={() => setDocumentIndex((index) => (index + 1) % documentCount)}>Next document</button></div>}
        </div>
        <input ref={fileRef} className="sr-only" type="file" accept="application/pdf,.pdf" onChange={(e) => { const form = packet.documents.find((item) => item.id === e.target.dataset.formId); if (form) submit(form, e.target.files?.[0]); }} />
      </>}
        </div>
      </div>
      </div>
      </div>
    </section>
    <TotoAdaptiveDock active="profile" onBack={() => navigate("/home")} backLabel="Today" />
  </main>;
}
